import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const RESERVATION_KEY = 'dj-cart-reservation-end-v2';
const DEFAULT_RESERVATION_SECONDS = 14 * 60 + 37;
const MAX_RECOMMENDATIONS = 5;
const MAX_RECOMMENDATION_SOURCES = 3;
const RECOMMENDATION_REQUEST_LIMIT = 10;
const RECOMMENDATION_LOAD_TIMEOUT = 4500;
const RECOMMENDATION_CACHE_TTL = 60 * 1000;
const ADD_REQUEST_TIMEOUT = 8000;
const PRODUCT_RESPONSE_CACHE = new Map();
const PRODUCT_FETCHED_AT = new WeakMap();
const RECOMMENDATION_RENDER_CACHE = new Map();
let LAST_RECOMMENDATIONS = [];
let VOLATILE_RESERVATION_END = 0;

function reservationDurationFromPage() {
  return (
    Number(document.querySelector('dj-cart-reservation')?.dataset.durationSeconds) ||
    DEFAULT_RESERVATION_SECONDS
  );
}

function readReservationEnd() {
  try {
    return Number(window.sessionStorage.getItem(RESERVATION_KEY)) || VOLATILE_RESERVATION_END;
  } catch {
    return VOLATILE_RESERVATION_END;
  }
}

function ensureReservationEnd(duration = reservationDurationFromPage()) {
  const existingEnd = readReservationEnd();
  if (existingEnd) return existingEnd;

  const end = Date.now() + duration * 1000;
  VOLATILE_RESERVATION_END = end;
  try {
    window.sessionStorage.setItem(RESERVATION_KEY, String(end));
  } catch {
    // The in-memory deadline still prevents a restart when storage is unavailable.
  }
  return end;
}

function handleReservationCartEvent(event) {
  const source = event.detail?.data?.source;
  const isAdd =
    event instanceof CartAddEvent ||
    source === 'product-form-component' ||
    source === 'dj-cart-recommendations';
  if (isAdd) ensureReservationEnd();
  if (!readReservationEnd()) return;

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('dj-cart-reservation').forEach((timer) => timer.sync?.());
    });
  });
}

class DjCartReservation extends HTMLElement {
  #interval;
  #endTime;

  connectedCallback() {
    window.queueMicrotask(() => {
      if (!this.isConnected) return;

      const hasCartRows = Boolean(this.closest('dialog')?.querySelector('tbody .cart-items__table-row'));
      if (!readReservationEnd() && hasCartRows) {
        ensureReservationEnd(Number(this.dataset.durationSeconds) || DEFAULT_RESERVATION_SECONDS);
      }
      this.sync();
    });
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.sync();
  }

  sync() {
    this.stop();
    this.#endTime = readReservationEnd();
    this.hidden = !this.#endTime;
    if (!this.#endTime) return;

    if (this.#tick()) this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  stop() {
    if (this.#interval) window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #getEndTime() {
    if (this.#endTime) return this.#endTime;
    this.#endTime = readReservationEnd();
    return this.#endTime;
  }

  #tick() {
    const copy = this.querySelector('[data-dj-cart-reservation-copy]');
    if (!copy) return true;

    const remaining = this.#getEndTime() - Date.now();
    if (remaining <= 0) {
      copy.textContent = this.dataset.expiredText || 'Reservationstiden har gått ut – varorna är nu tillgängliga för andra.';
      this.dataset.expired = 'true';
      this.stop();
      return false;
    }

    const output = this.querySelector('[data-dj-cart-timer]');
    if (!output) return true;

    const seconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(seconds / 60);
    output.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    return true;
  }
}

class DjCartRecommendations extends HTMLElement {
  #loadedKey = '';
  #loadingKey = '';
  #loadGeneration = 0;
  #loadFrame = 0;
  #abortController = new AbortController();
  #confirmedProductIds = new Set();
  #addInFlight = false;
  #activeAddButton;
  #lockedAddButtons = new Map();
  #cardSignatures = new WeakMap();
  #dismissVariantPickers = (event) => {
    this.querySelectorAll('.dj-cart-rec__variant-picker[open]').forEach((picker) => {
      if (!picker.contains(event.target)) picker.open = false;
    });
  };
  #drawer;
  #dialog;
  #railResizeObserver;
  #railFrame = 0;
  #queueRailUpdate = () => {
    if (this.#railFrame) return;
    this.#railFrame = window.requestAnimationFrame(() => {
      this.#railFrame = 0;
      this.#updateRailControls();
    });
  };
  #handleRailClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dj-recommendation-direction]') : null;
    const rail = this.querySelector('[data-dj-cart-recommendations-rail]');
    if (!button || !this.contains(button) || !rail || button.disabled) return;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    rail.scrollBy({
      left: Number(button.dataset.djRecommendationDirection) * (rail.clientWidth + gap - 4),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };
  #updateRailControls = () => {
    const rail = this.querySelector('[data-dj-cart-recommendations-rail]');
    const navigation = this.querySelector('[data-dj-recommendation-navigation]');
    if (!rail || !navigation) return;
    const cards = [...rail.querySelectorAll('.dj-cart-rec:not(.dj-cart-rec--skeleton)')];
    const overflow = rail.scrollWidth - rail.clientWidth;
    navigation.hidden = cards.length <= 2 || overflow < 2;
    const previous = navigation.querySelector('[data-dj-recommendation-direction="-1"]');
    const next = navigation.querySelector('[data-dj-recommendation-direction="1"]');
    if (previous) previous.disabled = rail.scrollLeft < 2;
    if (next) next.disabled = rail.scrollLeft >= overflow - 2;
    const position = navigation.querySelector('[data-dj-recommendation-position]');
    if (position && cards.length) {
      const step = cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      if (step <= 0) return;
      const start = Math.min(cards.length, Math.max(1, Math.round(rail.scrollLeft / step) + 1));
      position.textContent = `${start}–${Math.min(start + 1, cards.length)} av ${cards.length}`;
    }
  };
  #handleOpen = () => {
    this.#compactDrawerLayout();
    this.#prepareQuantityControls();
    this.#queueLoad();
    this.#queueRailUpdate();
  };
  #handleDiscountUpdate = () => {
    this.#invalidateLoad();
    this.#queueLoad();
  };
  #handleCartControlChange = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-dj-cart-line-variant]') : null;
    if (target instanceof HTMLSelectElement) this.#changeLineVariant(target);
  };
  #handleQuantityClick = (event) => {
    const minusButton = event.target instanceof Element ? event.target.closest('.quantity-minus') : null;
    const row = minusButton?.closest('.cart-items__table-row');
    const input = row?.querySelector('input[name="updates[]"], input[type="number"]');
    const removeButton = row?.querySelector('.cart-items__remove');

    if (
      row?.closest('dialog') !== this.#dialog ||
      !(minusButton instanceof HTMLButtonElement) ||
      !(removeButton instanceof HTMLButtonElement) ||
      removeButton.hidden ||
      removeButton.disabled ||
      Number(input?.value) !== 1
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const dialog = row.closest('dialog');
    const isFinalLine = dialog?.querySelectorAll('tbody .cart-items__table-row').length === 1;
    if (isFinalLine) this.#closeDrawerWhenEmpty();
    removeButton.click();
  };
  #closeDrawerWhenEmpty = () => {
    let attempts = 0;
    const closeAfterMorph = () => {
      const drawer =
        document.querySelector('cart-drawer-component[auto-open]') ||
        document.querySelector('cart-drawer-component');
      const dialog = drawer?.querySelector('dialog');
      const hasCartRows = Boolean(dialog?.querySelector('tbody .cart-items__table-row'));

      if (!hasCartRows) {
        if (!dialog?.open) return;
        if (typeof drawer.close === 'function') {
          drawer.close();
        } else {
          dialog.close();
        }
        return;
      }
      attempts += 1;
      if (attempts < 120) window.setTimeout(closeAfterMorph, 50);
    };

    window.setTimeout(closeAfterMorph, 50);
  };
  #invalidateLoad() {
    this.#loadGeneration += 1;
    this.#loadedKey = '';
    this.#loadingKey = '';
    if (this.#loadFrame) window.cancelAnimationFrame(this.#loadFrame);
    this.#loadFrame = 0;
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }
  #queueLoad() {
    if (this.#loadFrame) window.cancelAnimationFrame(this.#loadFrame);
    this.#loadFrame = window.requestAnimationFrame(() => {
      this.#loadFrame = 0;
      if (!this.isConnected || !this.closest('dialog')?.open) return;
      this.#compactDrawerLayout();
      this.#prepareQuantityControls();
      this.load();
    });
  }
  #isCurrentLoad(generation, cacheKey, controller) {
    return (
      this.isConnected &&
      generation === this.#loadGeneration &&
      cacheKey === this.#loadingKey &&
      controller === this.#abortController &&
      !controller.signal.aborted
    );
  }
  #finishWithoutProducts(cacheKey = '') {
    const list = this.querySelector('[data-dj-recommendation-list]');
    const hasRenderedProducts = Boolean(list?.querySelector('.dj-cart-rec:not(.dj-cart-rec--skeleton)'));
    this.setAttribute('aria-busy', 'false');
    if (hasRenderedProducts) {
      this.hidden = false;
      this.#loadedKey = cacheKey;
      return;
    }

    list?.replaceChildren();
    this.hidden = true;
    this.#loadedKey = '';
  }
  #applyDrawerSectionUpdate(cartData, { refreshRecommendations = false } = {}) {
    const sectionId = this.dataset.sectionId;
    const sectionHTML = cartData?.sections?.[sectionId];
    const currentDialog = this.closest('dialog') || this.#dialog;
    if (!sectionId || !sectionHTML || !currentDialog) return false;

    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextSection = parsed.getElementById(`shopify-section-${sectionId}`) || parsed;
    const nextDialog =
      nextSection.querySelector('cart-drawer-component .cart-drawer__dialog') ||
      nextSection.querySelector('.cart-drawer__dialog');
    const currentSection = document.getElementById(`shopify-section-${sectionId}`);
    const currentCartItems = currentDialog.querySelector('cart-items-component');
    const nextCartItems = nextDialog?.querySelector('cart-items-component');
    if (!nextDialog || !currentCartItems || !nextCartItems) return false;

    const regionSelectors = [
      '.cart-drawer__header',
      '.dj-cart-top',
      '.cart-items__wrapper',
      '.cart-drawer__summary',
    ];
    const regions = regionSelectors.map((selector) => ({
      current: currentCartItems.querySelector(selector),
      next: nextCartItems.querySelector(selector),
    }));
    const scrollContainer = currentDialog.querySelector('.cart-drawer__items');
    const scrollTop = scrollContainer?.scrollTop || 0;
    const currentProducts = this.querySelector('[data-dj-cart-products]');
    const nextProducts = nextCartItems.querySelector('[data-dj-cart-products]');
    if (currentProducts && nextProducts) currentProducts.textContent = nextProducts.textContent || '[]';

    // Keep the existing recommendation element mounted. Morphing the complete
    // cart component can replace it when a new cart row changes sibling order,
    // which collapses the list and makes the open drawer appear to close/reopen.
    if (regions.every(({ current, next }) => current && next)) {
      for (const { current, next } of regions) {
        try {
          morph(current, next);
        } catch {
          current.replaceWith(next);
        }
      }
    } else {
      const currentRecommendations = currentCartItems.querySelector('dj-cart-recommendations');
      const nextRecommendations = nextCartItems.querySelector('dj-cart-recommendations');
      try {
        currentRecommendations?.setAttribute('data-skip-subtree-update', '');
        nextRecommendations?.setAttribute('data-skip-subtree-update', '');
        morph(currentCartItems, nextCartItems);
      } finally {
        currentRecommendations?.removeAttribute('data-skip-subtree-update');
      }
    }
    currentCartItems.updatedCallback?.();

    const currentTrigger = currentSection?.querySelector('[data-testid="cart-drawer-trigger"]');
    const nextTrigger = nextSection.querySelector('[data-testid="cart-drawer-trigger"]');
    if (currentTrigger && nextTrigger) {
      const nextCount = nextTrigger.querySelector('.cart-bubble__text-count')?.textContent?.trim();
      const nextCountLabel = nextTrigger.querySelector('.cart-bubble .visually-hidden')?.textContent?.trim();
      try {
        morph(currentTrigger, nextTrigger);
      } catch {
        // The direct count sync below is sufficient if Horizon protects the trigger subtree.
      }
      if (nextCount) {
        document.querySelectorAll('.cart-bubble__text-count').forEach((count) => {
          count.textContent = nextCount;
        });
      }
      if (nextCountLabel) {
        document.querySelectorAll('.cart-bubble .visually-hidden').forEach((label) => {
          label.textContent = nextCountLabel;
        });
      }
    }

    this.#compactDrawerLayout();
    this.#prepareQuantityControls();
    currentDialog.querySelector('dj-cart-reservation')?.sync?.();
    window.requestAnimationFrame(() => {
      const nextScrollContainer = currentDialog.querySelector('.cart-drawer__items');
      if (nextScrollContainer) nextScrollContainer.scrollTop = scrollTop;
    });

    if (refreshRecommendations) this.#refreshRecommendations();
    return true;
  }
  #refreshRecommendations() {
    const cartIds = new Set(this.#getSources().map((source) => String(source.id)));
    this.#confirmedProductIds.forEach((id) => cartIds.add(String(id)));
    this.querySelectorAll('.dj-cart-rec:not(.dj-cart-rec--skeleton)').forEach((card) => {
      if (cartIds.has(String(card.dataset.productId))) card.remove();
    });

    this.#invalidateLoad();
    this.#queueLoad();
  }
  async #changeLineVariant(select) {
    const currentVariantId = select.dataset.currentVariant;
    const nextVariantId = select.value;
    if (select.disabled || !select.selectedOptions[0] || select.selectedOptions[0].disabled) {
      select.value = currentVariantId || '';
      return;
    }
    const lineKey = select.dataset.lineKey;
    const sectionId = this.dataset.sectionId;
    const row = select.closest('.cart-items__table-row');
    const quantityInput = row?.querySelector('input[name="updates[]"], input[type="number"]');
    const quantity = Math.max(1, Number(quantityInput?.value || select.dataset.quantity) || 1);

    if (!currentVariantId || !nextVariantId || !lineKey || !sectionId || currentVariantId === nextVariantId) return;

    let properties = {};
    try {
      properties = JSON.parse(select.dataset.properties || '{}');
    } catch {
      properties = {};
    }

    const matchingLine = [...(this.#dialog?.querySelectorAll('[data-dj-cart-line-variant]') || [])].find(
      (item) =>
        item !== select &&
        item.dataset.currentVariant === nextVariantId &&
        item.dataset.properties === select.dataset.properties &&
        item.dataset.sellingPlan === select.dataset.sellingPlan
    );
    const matchingRow = matchingLine?.closest('.cart-items__table-row');
    const matchingQuantity = Number(
      matchingRow?.querySelector('input[name="updates[]"], input[type="number"]')?.value || 0
    );

    const item = { id: Number(nextVariantId), quantity };
    if (properties && Object.keys(properties).length) item.properties = properties;
    if (select.dataset.sellingPlan) item.selling_plan = Number(select.dataset.sellingPlan);

    const originalLabel = select.getAttribute('aria-label') || 'Välj variant';
    const root = window.Shopify?.routes?.root || '/';
    let addedLineKey = '';

    row?.setAttribute('aria-busy', 'true');
    select.disabled = true;

    try {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const addedData = await addResponse.json();
      if (!addResponse.ok || addedData.status) {
        throw new Error(addedData.description || addedData.message || 'Kunde inte byta variant.');
      }

      addedLineKey = String(addedData.items?.[0]?.key || addedData.key || nextVariantId);

      const removeResponse = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const cartData = await removeResponse.json();
      if (!removeResponse.ok || cartData.status || cartData.errors) {
        throw new Error(cartData.description || cartData.message || cartData.errors || 'Kunde inte byta variant.');
      }

      if (!this.#applyDrawerSectionUpdate(cartData)) {
        this.dispatchEvent(
          new CartUpdateEvent(cartData, sectionId, {
            source: 'dj-cart-line-variant',
            itemCount: Number(cartData.item_count) || 0,
            variantId: nextVariantId,
            sections: cartData.sections,
          })
        );
      }
    } catch (error) {
      if (addedLineKey) {
        try {
          await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: addedLineKey, quantity: matchingQuantity }),
          });
        } catch {
          // The next cart render reconciles any rare rollback failure.
        }
      }

      select.value = currentVariantId;
      select.setAttribute('aria-invalid', 'true');
      select.setAttribute('aria-label', error?.message || 'Kunde inte byta variant');
      window.setTimeout(() => {
        if (!select.isConnected) return;
        select.removeAttribute('aria-invalid');
        select.setAttribute('aria-label', originalLabel);
      }, 2500);
    } finally {
      if (select.isConnected) select.disabled = false;
      row?.removeAttribute('aria-busy');
    }
  }

  #prepareQuantityControls = () => {
    const dialog = this.#dialog || this.closest('dialog');
    dialog?.querySelectorAll('tbody .cart-items__table-row').forEach((row) => {
      const input = row.querySelector('input[name="updates[]"], input[type="number"]');
      const minusButton = row.querySelector('.quantity-minus');
      if (Number(input?.value) === 1 && minusButton instanceof HTMLButtonElement && minusButton.disabled) {
        minusButton.disabled = false;
      }
    });
  };
  #handleCartUpdate = (event) => {
    this.#invalidateLoad();
    this.#queueLoad();
  };

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#compactDrawerLayout();
    this.#drawer = this.closest('cart-drawer-component');
    this.#dialog = this.closest('dialog');
    this.#drawer?.addEventListener('dj:cart-drawer-open', this.#handleOpen);
    window.addEventListener('click', this.#handleQuantityClick, true);
    this.#dialog?.addEventListener('change', this.#handleCartControlChange);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleDiscountUpdate);
    this.addEventListener('click', this.#handleRailClick);
    document.addEventListener('pointerdown', this.#dismissVariantPickers);
    this.addEventListener('scroll', this.#queueRailUpdate, true);
    this.#railResizeObserver = new ResizeObserver(this.#queueRailUpdate);
    this.#railResizeObserver.observe(this);
    this.#queueRailUpdate();
    this.#prepareQuantityControls();
    window.requestAnimationFrame(this.#prepareQuantityControls);
    if (this.dataset.enabled === 'false') {
      this.hidden = true;
      return;
    }
    if (this.closest('dialog')?.open) this.#queueLoad();
  }

  #compactDrawerLayout() {
    const summary = this.closest('dialog')?.querySelector('.cart-drawer__summary');
    if (!(summary instanceof HTMLElement)) return;

    const cartActions = summary.querySelector('.cart-actions');
    if (cartActions) {
      const showNote = this.closest('dialog')?.dataset.showNote !== 'false';
      const showDiscount = this.closest('dialog')?.dataset.showDiscount !== 'false';
      const note = cartActions.querySelector('cart-note');
      const discount = cartActions.querySelector('disclosure-custom.cart-discount');
      const divider = cartActions.querySelector('.cart-actions__divider');
      if (note) note.hidden = !showNote;
      if (discount) discount.hidden = !showDiscount;
      if (divider) divider.hidden = !(showNote && showDiscount);

      const noteLabel = note?.querySelector('.cart-note__label');
      const discountLabel = discount?.querySelector('.disclosure-trigger__label');
      if (noteLabel && this.closest('dialog')?.dataset.noteLabel) {
        noteLabel.textContent = this.closest('dialog').dataset.noteLabel;
      }
      if (discountLabel && this.closest('dialog')?.dataset.discountLabel) {
        discountLabel.textContent = this.closest('dialog').dataset.discountLabel;
      }

      const noteDetails = note?.querySelector('details');
      if (noteDetails instanceof HTMLDetailsElement && note?.dataset.djNoteEditing !== 'true') {
        noteDetails.open = false;
      }

      const discountTrigger = discount?.querySelector('.disclosure-trigger');
      const discountContent = discount?.querySelector('.disclosure-content');
      if (discountTrigger instanceof HTMLButtonElement) {
        discountTrigger.setAttribute('aria-expanded', 'false');
        if (discountTrigger.dataset.disclosureOpen) {
          discountTrigger.setAttribute('aria-label', discountTrigger.dataset.disclosureOpen);
        }
      }
      if (discountContent instanceof HTMLElement) discountContent.setAttribute('inert', '');

      cartActions.hidden = !showNote && !showDiscount;
    }

    const footer = summary.querySelector('.dj-cart-footer');
    if (footer) {
      const dialog = this.closest('dialog');
      const showPayments = dialog?.dataset.showPayments !== 'false';
      const paymentCopy = footer.querySelector('.dj-cart-payment-copy');
      const paymentIcons = footer.querySelector('.dj-cart-payment-icons');
      const trustline = footer.querySelector('.dj-cart-trustline');
      const guarantee = footer.querySelector('.dj-cart-guarantee');
      const showGuarantee = Boolean(guarantee?.textContent.trim());
      if (paymentCopy) paymentCopy.hidden = !showPayments;
      if (paymentIcons) paymentIcons.hidden = !showPayments;
      if (trustline) trustline.hidden = !showPayments || !trustline.textContent.trim();
      if (guarantee) guarantee.hidden = !showGuarantee;

      // The secondary continue-shopping action remains available even without badges.
      footer.hidden = false;
    }

    const checkout = summary.querySelector('.cart__checkout-button');
    const checkoutLabel = this.closest('dialog')?.dataset.checkoutLabel;
    const checkoutContent = checkout?.querySelector('[data-dj-checkout-content]');
    const checkoutPrice = checkoutContent?.dataset.djCheckoutPrice;
    if (checkoutContent && checkoutLabel && checkoutPrice) {
      checkoutContent.textContent = `${checkoutLabel} · ${checkoutPrice}`;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#dismissVariantPickers);
    this.#drawer?.removeEventListener('dj:cart-drawer-open', this.#handleOpen);
    window.removeEventListener('click', this.#handleQuantityClick, true);
    this.#dialog?.removeEventListener('change', this.#handleCartControlChange);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleDiscountUpdate);
    this.removeEventListener('click', this.#handleRailClick);
    this.removeEventListener('scroll', this.#queueRailUpdate, true);
    this.#railResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.#railFrame);
    this.#railFrame = 0;
    this.#invalidateLoad();
  }

  async load() {
    if (this.dataset.enabled === 'false') {
      this.hidden = true;
      return;
    }

    const sources = this.#getSources();
    if (!sources.length) {
      this.#finishWithoutProducts();
      return;
    }

    const cacheKey = this.#recommendationCacheKey(sources);
    const cartIds = new Set(sources.map((source) => String(source.id)));
    this.querySelectorAll('.dj-cart-rec:not(.dj-cart-rec--skeleton)').forEach((card) => {
      if (cartIds.has(String(card.dataset.productId)) || this.#confirmedProductIds.has(String(card.dataset.productId))) {
        card.remove();
      }
    });

    if (this.#loadingKey === cacheKey) return;
    if (this.#loadingKey) {
      this.#loadGeneration += 1;
      this.#loadingKey = '';
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    const hasRenderedProducts = Boolean(
      this.querySelector('.dj-cart-rec:not(.dj-cart-rec--skeleton)')
    );
    const hasSkeletons = Boolean(this.querySelector('.dj-cart-rec--skeleton'));
    const cachedResult = RECOMMENDATION_RENDER_CACHE.get(cacheKey);
    const cacheIsFresh = cachedResult && Date.now() - cachedResult.fetchedAt < RECOMMENDATION_CACHE_TTL;
    if (this.#loadedKey === cacheKey && hasRenderedProducts && !hasSkeletons && cacheIsFresh) {
      this.hidden = false;
      this.setAttribute('aria-busy', 'false');
      return;
    }

    const shouldRefresh = this.#loadedKey !== cacheKey || !cacheIsFresh;
    const cachedProducts = cachedResult?.products;
    let renderedFromCache = false;
    if (cachedProducts?.length) {
      const validCachedProducts = this.#selectProducts(
        [{ source: sources[0], products: cachedProducts }],
        sources
      );
      if (validCachedProducts.length) {
        this.hidden = false;
        this.#render(validCachedProducts);
        renderedFromCache = true;
        this.#loadedKey = cacheKey;
        this.setAttribute('aria-busy', 'false');
        if (!shouldRefresh) return;
      }
    }

    const immediateProducts = this.#selectProducts(
      [{ source: sources[0], products: LAST_RECOMMENDATIONS }],
      sources
    );
    const hasCurrentRenderedProducts = Boolean(
      this.querySelector('.dj-cart-rec:not(.dj-cart-rec--skeleton)')
    );
    this.hidden = false;
    if (!renderedFromCache && immediateProducts.length) {
      this.#render(immediateProducts);
      this.setAttribute('aria-busy', 'false');
    } else if (hasCurrentRenderedProducts) {
      this.setAttribute('aria-busy', 'false');
    } else {
      this.#showSkeletons();
      this.setAttribute('aria-busy', 'true');
    }

    this.#abortController.abort();
    this.#abortController = new AbortController();
    const controller = this.#abortController;
    const generation = ++this.#loadGeneration;
    this.#loadingKey = cacheKey;
    const loadTimeout = window.setTimeout(() => {
      if (!this.#isCurrentLoad(generation, cacheKey, controller)) return;
      this.#finishWithoutProducts(cacheKey);
      this.#loadGeneration += 1;
      this.#loadingKey = '';
      controller.abort();
    }, RECOMMENDATION_LOAD_TIMEOUT);

    try {
      const root = window.Shopify?.routes?.root || '/';
      const recommendationSources = sources.slice(0, MAX_RECOMMENDATION_SOURCES);
      const complementaryResults = await Promise.all(
        recommendationSources.map((source) =>
          this.#fetchRecommendations(root, source, 'complementary', controller.signal)
        )
      );
      if (!this.#isCurrentLoad(generation, cacheKey, controller)) return;

      const products = this.#selectProducts(complementaryResults, sources);
      if (products.length) {
        this.hidden = false;
        this.#render([...products]);
        this.#loadedKey = cacheKey;
        this.setAttribute('aria-busy', 'false');
      }

      if (this.dataset.fill !== 'false' && products.length < this.#getLimit()) {
        const relatedResults = await Promise.all(
          recommendationSources.map((source) =>
            this.#fetchRecommendations(root, source, 'related', controller.signal)
          )
        );
        if (!this.#isCurrentLoad(generation, cacheKey, controller)) return;

        this.#selectProducts(relatedResults, sources, products);
        if (products.length) {
          this.hidden = false;
          this.#render([...products]);
          this.#loadedKey = cacheKey;
          this.setAttribute('aria-busy', 'false');
        }
      }

      if (this.dataset.fill !== 'false' && products.length < this.#getLimit()) {
        const fallbackProducts = await this.#fetchFallbackProducts(root, controller.signal);
        if (!this.#isCurrentLoad(generation, cacheKey, controller)) return;

        this.#selectProducts([{ source: recommendationSources[0], products: fallbackProducts }], sources, products);
      }

      if (!this.#isCurrentLoad(generation, cacheKey, controller)) return;
      if (!products.length) {
        this.#finishWithoutProducts(cacheKey);
        return;
      }

      RECOMMENDATION_RENDER_CACHE.set(cacheKey, {
        products,
        fetchedAt: Math.min(...products.map((product) => PRODUCT_FETCHED_AT.get(product) || 0)),
      });
      LAST_RECOMMENDATIONS = products;
      this.#render(products);
      this.#loadedKey = cacheKey;
      this.hidden = false;
      this.setAttribute('aria-busy', 'false');
    } catch (error) {
      if (!this.#isCurrentLoad(generation, cacheKey, controller) || error?.name === 'AbortError') return;
      console.error('Cart recommendations failed', error);
      this.#finishWithoutProducts(cacheKey);
    } finally {
      window.clearTimeout(loadTimeout);
      if (generation === this.#loadGeneration && this.#loadingKey === cacheKey) {
        this.#loadingKey = '';
      }
    }
  }

  #getSources() {
    const dataNode = this.querySelector('[data-dj-cart-products]');
    if (!dataNode?.textContent) return [];

    try {
      const parsed = JSON.parse(dataNode.textContent);
      const seen = new Set();
      const sources = parsed.filter((product) => {
        const id = String(product.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const sourceIds = new Set(sources.map((source) => String(source.id)));
      this.#confirmedProductIds.forEach((id) => {
        if (sourceIds.has(id)) this.#confirmedProductIds.delete(id);
      });
      return sources;
    } catch {
      return [];
    }
  }

  async #fetchRecommendations(root, source, intent, signal) {
    const url = new URL(`${root}recommendations/products.json`, window.location.origin);
    url.searchParams.set('product_id', source.id);
    url.searchParams.set('limit', String(RECOMMENDATION_REQUEST_LIMIT));
    url.searchParams.set('intent', intent);
    return { source, products: await this.#fetchProducts(url, signal) };
  }

  async #fetchFallbackProducts(root, signal) {
    const url = new URL(`${root}collections/all/products.json`, window.location.origin);
    url.searchParams.set('limit', '24');
    url.searchParams.set('sort_by', 'best-selling');
    return this.#fetchProducts(url, signal);
  }

  async #fetchProducts(url, parentSignal) {
    const cacheKey = url.toString();
    const cached = PRODUCT_RESPONSE_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < RECOMMENDATION_CACHE_TTL) return cached.products;
    if (parentSignal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const requestController = new AbortController();
    const cancelRequest = () => requestController.abort();
    const timeout = window.setTimeout(cancelRequest, RECOMMENDATION_LOAD_TIMEOUT);
    parentSignal?.addEventListener('abort', cancelRequest, { once: true });

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: requestController.signal,
      });
      if (!response.ok) return [];

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      const fetchedAt = Date.now();
      products.forEach((product) => PRODUCT_FETCHED_AT.set(product, fetchedAt));
      PRODUCT_RESPONSE_CACHE.set(cacheKey, { products, fetchedAt });
      return products;
    } catch (error) {
      if (parentSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return [];
    } finally {
      window.clearTimeout(timeout);
      parentSignal?.removeEventListener('abort', cancelRequest);
    }
  }

  #selectProducts(sourceResults, cartSources, selected = []) {
    const cartIds = new Set(cartSources.map((source) => String(source.id)));
    this.#confirmedProductIds.forEach((id) => cartIds.add(String(id)));
    const selectedIds = new Set(selected.map((product) => String(product.id)));

    for (const result of sourceResults) {
      for (const product of result.products) {
        const variants = (product.variants || []).filter((variant) => variant.available === true);
        const productId = String(product.id);
        if (!product.available || !variants.length || cartIds.has(productId) || selectedIds.has(productId)) {
          continue;
        }

        selectedIds.add(productId);
        selected.push(product);
        if (selected.length === this.#getLimit()) return selected;
      }
    }

    return selected;
  }

  #getLimit() {
    const configuredLimit = Number(this.dataset.limit) || MAX_RECOMMENDATIONS;
    return Math.min(MAX_RECOMMENDATIONS, Math.max(1, configuredLimit));
  }

  #recommendationCacheKey(sources) {
    return [
      sources.map((source) => String(source.id)).join(','),
      this.#getLimit(),
      this.dataset.fill !== 'false' ? 'fill' : 'complementary',
    ].join('|');
  }

  #showSkeletons() {
    const list = this.querySelector('[data-dj-recommendation-list]');
    if (!list) return;

    const skeletons = Array.from({ length: Math.min(3, this.#getLimit()) }, () => {
      const card = document.createElement('article');
      card.className = 'dj-cart-rec dj-cart-rec--skeleton';
      card.setAttribute('aria-hidden', 'true');

      const image = document.createElement('span');
      image.className = 'dj-cart-rec__skeleton-image';
      const title = document.createElement('span');
      title.className = 'dj-cart-rec__skeleton-title';
      const copy = document.createElement('span');
      copy.className = 'dj-cart-rec__skeleton-copy';
      card.append(title, image, copy);
      return card;
    });
    list.replaceChildren(...skeletons);
    this.#queueRailUpdate();
  }

  #render(products) {
    const list = this.querySelector('[data-dj-recommendation-list]');
    if (!list) return;

    const previousScroll = list.scrollLeft;
    const existing = new Map([...list.querySelectorAll('.dj-cart-rec')].map((card) => [card.dataset.productId, card]));
    const cards = products.map((product) => {
      const current = existing.get(String(product.id));
      if (current && this.#cardSignatures.get(current) === JSON.stringify(product)) return current;
      const selectedId = current?.querySelector('.dj-cart-rec__variant')?.value;
      return this.#createCard(product, selectedId);
    });
    // Keep unchanged nodes mounted so a background fill cannot reset an open picker.
    [...list.children].forEach((node) => { if (!cards.includes(node)) node.remove(); });
    cards.forEach((card, index) => {
      if (list.children[index] !== card) list.insertBefore(card, list.children[index] || null);
    });
    list.scrollLeft = previousScroll;
    this.#queueRailUpdate();
    if (this.#addInFlight) this.#setRecommendationAddLock(true);
    const status = this.querySelector('[data-dj-recommendation-status]');
    if (status) status.textContent = `${products.length} produktförslag har laddats.`;
  }

  #createCard(product, selectedId) {
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    const requiresPhoto = tags.some((tag) => String(tag).trim() === 'custom-bilduppladdning');
    const variants = product.variants || [];
    const availableVariants = variants.filter((variant) => variant.available === true);
    const initialVariant = availableVariants.find((variant) => String(variant.id) === selectedId) || availableVariants[0];
    let selectedVariant = initialVariant;
    const card = document.createElement('article');
    card.className = 'dj-cart-rec';
    card.dataset.productId = String(product.id);
    this.#cardSignatures.set(card, JSON.stringify(product));

    const imageLink = document.createElement('a');
    imageLink.className = 'dj-cart-rec__media';
    imageLink.href = product.url || `/products/${product.handle}`;
    imageLink.setAttribute('aria-label', product.title);

    const image = document.createElement('img');
    image.loading = 'lazy';
    image.width = 148;
    image.height = 148;
    image.alt = product.title;
    imageLink.append(image);

    const content = document.createElement('div');
    content.className = 'dj-cart-rec__content';

    const title = document.createElement('a');
    title.className = 'dj-cart-rec__title';
    title.href = imageLink.href;
    title.textContent = product.title;
    title.title = product.title;

    let select;
    let picker;
    if (variants.length > 1) {
      select = document.createElement('select');
      select.className = 'dj-cart-rec__variant';
      select.setAttribute('aria-label', `Välj variant för ${product.title}`);
      for (const variant of variants) {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.title}${variant.available === true ? '' : ' – Slutsåld'}`;
        option.disabled = variant.available !== true;
        select.append(option);
      }
      select.value = String(initialVariant.id);
      select.hidden = true;
      select.tabIndex = -1;
      picker = this.#createVariantPicker(select, variants, product);
      content.append(select, picker.element);
    }

    const price = document.createElement('span');
    price.className = 'dj-cart-rec__price';
    content.append(price);

    const button = document.createElement(requiresPhoto ? 'a' : 'button');
    if (!requiresPhoto) button.type = 'button';
    button.className = 'dj-cart-rec__add';
    button.dataset.addLabel = this.dataset.addLabel || 'Lägg till';
    if (requiresPhoto) {
      button.textContent = 'Anpassa';
      button.setAttribute('aria-label', `Anpassa ${product.title} och ladda upp bild`);
      button.style.display = 'inline-flex';
      button.style.alignItems = 'center';
      button.style.justifyContent = 'center';
      button.style.textDecoration = 'none';
    }

    const updateVariant = () => {
      const variant = availableVariants.find((item) => String(item.id) === String(select?.value)) || selectedVariant;
      selectedVariant = variant;
      if (select) select.value = String(variant.id);
      picker?.sync();
      const variantName = variant.title && variant.title !== 'Default Title' ? `, ${variant.title}` : '';
      button.dataset.accessibleName = `${product.title}${variantName}`;
      if (requiresPhoto) {
        const photoUrl = new URL(imageLink.href, window.location.origin);
        photoUrl.searchParams.set('variant', String(variant.id));
        photoUrl.hash = 'dj-product-photo';
        button.href = photoUrl.href;
        button.setAttribute('aria-label', `Anpassa ${button.dataset.accessibleName} och ladda upp bild`);
      } else if (!button.dataset.addState || button.dataset.addState === 'idle' || button.dataset.addState === 'error') {
        this.#setRecommendationAddState(button, 'idle');
      }
      const current = document.createElement('span');
      current.className = 'dj-cart-rec__current';
      current.textContent = this.#formatMoney(variant.price);
      price.replaceChildren(current);

      if (Number(variant.compare_at_price) > Number(variant.price)) {
        const compare = document.createElement('span');
        compare.className = 'dj-cart-rec__compare';
        compare.textContent = this.#formatMoney(variant.compare_at_price);

        const savings = document.createElement('span');
        savings.className = 'dj-cart-rec__savings';
        savings.textContent = `Spara ${this.#formatSavings(Number(variant.compare_at_price) - Number(variant.price))}`;
        price.append(compare, savings);
      }

      const nextImage = this.#variantImage(variant, product);
      if (nextImage) image.setAttribute('src', nextImage);
      else image.removeAttribute('src');
      image.hidden = !nextImage;
      image.alt = `${product.title}${variantName}`;
    };

    select?.addEventListener('change', updateVariant);
    if (!requiresPhoto) {
      button.addEventListener('click', () => {
        const variantId = select?.value || String(initialVariant.id);
        if (!availableVariants.some((variant) => String(variant.id) === variantId)) {
          updateVariant();
          return;
        }
        this.#addToCart(button, variantId, String(product.id));
      });
    }

    updateVariant();
    // Names remain visible before the media, without reducing the image or touch targets.
    // Move the same controls before mounting; their listeners and state stay intact.
    const visual = document.createElement('div');
    visual.className = 'dj-cart-rec__visual';
    visual.append(imageLink, button);
    card.append(title, visual, content);
    return card;
  }

  #createVariantPicker(select, variants, product) {
    const element = document.createElement('details');
    element.className = 'dj-cart-rec__variant-picker';
    const summary = document.createElement('summary');
    summary.className = 'dj-cart-rec__variant-trigger';
    const label = document.createElement('span');
    label.className = 'dj-cart-rec__variant-label';
    summary.append(label);

    const options = document.createElement('fieldset');
    options.className = 'dj-cart-rec__variant-options';
    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = `Välj variant för ${product.title}`;
    options.append(legend);
    const radios = variants.map((variant) => {
      const row = document.createElement('label');
      row.className = 'dj-cart-rec__variant-option';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = `dj-cart-rec-variant-${product.id}`;
      radio.value = String(variant.id);
      radio.disabled = variant.available !== true;
      const text = document.createElement('span');
      text.textContent = `${variant.title}${radio.disabled ? ' – Slutsåld' : ''}`;
      row.append(radio, text);
      options.append(row);
      const choose = () => {
        if (radio.disabled) return;
        if (select.value !== radio.value) {
          select.value = radio.value;
          select.dispatchEvent(new Event('change'));
        }
        element.open = false;
        summary.focus({ preventScroll: true });
      };
      radio.addEventListener('change', choose);
      // The already-selected option must also close, without a redundant update.
      radio.addEventListener('click', choose);
      row.addEventListener('click', (event) => {
        if (event.target === radio || radio.disabled) return;
        // Select the whole label once, without a second native forwarded click.
        event.preventDefault();
        choose();
      });
      return radio;
    });
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      const opening = !element.open;
      this.querySelectorAll('.dj-cart-rec__variant-picker[open]').forEach((other) => {
        if (other !== element) other.open = false;
      });
      element.open = opening;
    });
    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !element.open) return;
      event.preventDefault();
      event.stopPropagation();
      element.open = false;
      summary.focus({ preventScroll: true });
    });
    element.addEventListener('focusout', (event) => {
      // A label press first focuses the scrollable rail, before its radio activates.
      // Do not collapse during that temporary ancestor-focus step.
      if (event.relatedTarget instanceof Element && event.relatedTarget.contains(element)) return;
      if (event.relatedTarget && !element.contains(event.relatedTarget)) element.open = false;
    });
    element.append(summary, options);
    return {
      element,
      sync: () => {
        const selected = variants.find((variant) => String(variant.id) === select.value) || variants[0];
        label.textContent = selected.title;
        summary.setAttribute('aria-label', `Välj variant för ${product.title}: ${selected.title}`);
        radios.forEach((radio) => { radio.checked = radio.value === select.value; });
      },
    };
  }

  #setRecommendationAddState(button, state, { message = '', announce = false } = {}) {
    const action = {
      idle: button.dataset.addLabel || this.dataset.addLabel || 'Lägg till',
      loading: 'Lägger till…',
      success: 'Tillagd',
      error: 'Försök igen',
    }[state];
    const name = state === 'idle'
      ? button.dataset.accessibleName
      : button.dataset.pendingAccessibleName || button.dataset.accessibleName;
    const label = name ? `${action}: ${name}` : action;
    button.dataset.addState = state;
    button.dataset.addStateVersion = String(Number(button.dataset.addStateVersion || 0) + 1);
    button.setAttribute('aria-label', label);
    button.setAttribute('title', message ? `${label}. ${message}` : label);
    button.setAttribute('aria-busy', String(state === 'loading'));

    // Keep the original click target inside the dialog while the click bubbles.
    // Replacing these nodes during loading can look like an outside click.
    let icon = button.querySelector('.dj-cart-rec__add-icon');
    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'dj-cart-rec__add-icon';
      icon.setAttribute('aria-hidden', 'true');
      button.append(icon);
    }
    icon.textContent = { idle: '', loading: '', success: '✓', error: '↻' }[state];
    let text = button.querySelector('.visually-hidden');
    if (!text) {
      text = document.createElement('span');
      text.className = 'visually-hidden';
      button.append(text);
    }
    text.textContent = label;

    if (announce) {
      const status = this.querySelector('[data-dj-recommendation-status]');
      if (status) status.textContent = message ? `${label}. ${message}` : label;
    }
  }

  #findCartRowByVariant(variantId) {
    return [...(this.#dialog?.querySelectorAll('tbody .cart-items__table-row') || [])].find((row) => {
      const selector = row.querySelector('cart-quantity-selector-component[data-variant-id]');
      return selector?.dataset.variantId === String(variantId);
    });
  }

  #renderedVariantQuantity(variantId) {
    return [...(this.#dialog?.querySelectorAll('tbody .cart-items__table-row') || [])].reduce((quantity, row) => {
      const selector = row.querySelector('cart-quantity-selector-component[data-variant-id]');
      if (selector?.dataset.variantId !== String(variantId)) return quantity;

      const input = row.querySelector('input[name="updates[]"], input[type="number"]');
      return quantity + Math.max(0, Number(input?.value) || 0);
    }, 0);
  }

  #renderedLineQuantity(lineKey) {
    if (!lineKey) return null;
    const row = [...(this.#dialog?.querySelectorAll('tbody .cart-items__table-row[data-key]') || [])].find(
      (item) => item.dataset.key === String(lineKey)
    );
    if (!row) return null;

    const input = row.querySelector('input[name="updates[]"], input[type="number"]');
    const quantity = Number(input?.value);
    return Number.isFinite(quantity) ? quantity : null;
  }

  #renderedItemCount() {
    const count = Number(this.#dialog?.querySelector('[ref="cartItemCount"]')?.textContent?.trim());
    return Number.isFinite(count) ? Math.max(0, count) : 0;
  }

  #setRecommendationAddLock(locked) {
    if (locked) {
      this.querySelectorAll('.dj-cart-rec__add').forEach((candidate) => {
        if (!(candidate instanceof HTMLButtonElement)) return;
        if (candidate !== this.#activeAddButton && !this.#lockedAddButtons.has(candidate)) {
          this.#lockedAddButtons.set(candidate, candidate.disabled);
        }
        candidate.disabled = true;
      });
      return;
    }

    for (const [candidate, wasDisabled] of this.#lockedAddButtons) {
      candidate.disabled = wasDisabled;
    }
    this.#lockedAddButtons.clear();
  }

  #applyExistingLineUpdate(cartData, variantId, row) {
    const item = cartData?.items?.find((cartItem) => String(cartItem.variant_id) === String(variantId));
    const itemCount = Number(cartData?.item_count);
    const totalPrice = Number(cartData?.total_price);
    const quantity = Number(item?.quantity);
    if (!row?.isConnected || !item || !Number.isFinite(itemCount) || !Number.isFinite(totalPrice) || quantity < 1) {
      return false;
    }

    const quantitySelector = row.querySelector('cart-quantity-selector-component[data-variant-id]');
    const input = row.querySelector('input[name="updates[]"], input[type="number"]');
    if (input instanceof HTMLInputElement) {
      input.value = String(quantity);
      input.defaultValue = String(quantity);
      input.setAttribute('value', String(quantity));
      input.setAttribute('data-cart-quantity', String(quantity));
    }
    quantitySelector?.setAttribute('data-cart-quantity', String(quantity));
    quantitySelector?.updateButtonStates?.();

    const linePrice = row.querySelector('.cart-items__line-prices text-component');
    const formattedLinePrice = this.#formatMoney(item.final_line_price ?? item.line_price);
    if (linePrice) {
      linePrice.textContent = formattedLinePrice;
      linePrice.setAttribute('value', formattedLinePrice);
    }

    const formattedCount = String(itemCount);
    document.querySelectorAll('.cart-bubble__text-count').forEach((count) => {
      count.textContent = formattedCount;
    });
    document.querySelectorAll('.cart-bubble .visually-hidden').forEach((label) => {
      label.textContent = `Totalt antal artiklar i varukorgen: ${formattedCount}`;
    });

    const checkoutContent = this.#dialog?.querySelector('[data-dj-checkout-content]');
    const checkoutLabel = this.#dialog?.dataset.checkoutLabel || 'Till kassan';
    const formattedTotal = this.#formatMoney(totalPrice);
    if (checkoutContent) {
      checkoutContent.dataset.djCheckoutPrice = formattedTotal;
      checkoutContent.textContent = `${checkoutLabel} · ${formattedTotal}`;
    }

    this.#prepareQuantityControls();
    this.#refreshRecommendations();
    return true;
  }

  async #addToCart(button, variantId, productId) {
    if (this.#addInFlight) return;

    this.#addInFlight = true;
    this.#activeAddButton = button;
    this.#setRecommendationAddLock(true);

    try {
      await this.#performAddToCart(button, variantId, productId);
    } finally {
      this.#addInFlight = false;
      this.#setRecommendationAddLock(false);
      this.#activeAddButton = undefined;
    }
  }

  async #performAddToCart(button, variantId, productId) {
    button.dataset.pendingAccessibleName = button.dataset.accessibleName;
    const itemCountBefore = this.#renderedItemCount();
    const quantityBefore = this.#renderedVariantQuantity(variantId);
    const root = window.Shopify?.routes?.root || '/';
    const sectionId = this.dataset.sectionId;
    let productWasAdded = false;
    let fallbackSectionFetched = false;
    let expectedLineKey = '';
    let expectedLineQuantity = 0;
    let hasExactLineExpectation = false;

    const isAddedQuantityRendered = () => {
      if (this.#renderedItemCount() < itemCountBefore + 1) return false;

      const exactLineQuantity = hasExactLineExpectation
        ? this.#renderedLineQuantity(expectedLineKey)
        : null;
      if (exactLineQuantity !== null) return exactLineQuantity >= expectedLineQuantity;

      // Discounts and other line characteristics can change Shopify's line key
      // between the add response and the rendered Liquid section. In that case,
      // verify the same variant's total rendered quantity instead of waiting for
      // a key that no longer exists.
      return this.#renderedVariantQuantity(variantId) >= quantityBefore + 1;
    };

    const fetchCurrentSection = async (baseData = {}) => {
      if (!sectionId) throw new Error('Varukorgen kunde inte uppdateras direkt.');
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('section_id', sectionId);
      sectionUrl.searchParams.set('_dj_cart', String(Date.now()));
      const sectionController = new AbortController();
      const sectionTimeout = window.setTimeout(() => sectionController.abort(), 4500);

      try {
        const sectionResponse = await fetch(sectionUrl, {
          cache: 'no-store',
          headers: { Accept: 'text/html' },
          signal: sectionController.signal,
        });
        if (!sectionResponse.ok) throw new Error('Varukorgen kunde inte uppdateras direkt.');

        const sectionHTML = await sectionResponse.text();
        fallbackSectionFetched = true;
        return {
          ...baseData,
          sections: { [sectionId]: sectionHTML },
        };
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('Varukorgen tog för lång tid att uppdatera.');
        }
        throw error;
      } finally {
        window.clearTimeout(sectionTimeout);
      }
    };

    button.disabled = true;
    this.#setRecommendationAddState(button, 'loading', { announce: true });

    try {
      const addController = new AbortController();
      const addTimeout = window.setTimeout(() => addController.abort(), ADD_REQUEST_TIMEOUT);
      let response;
      let data;

      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          signal: addController.signal,
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (error) {
        if (error?.name !== 'AbortError') throw error;

        const recoveredData = await fetchCurrentSection();
        const sectionApplied = this.#applyDrawerSectionUpdate(recoveredData);
        if (!sectionApplied || !isAddedQuantityRendered()) {
          throw new Error('Kunde inte bekräfta om produkten lades till. Öppna varukorgen igen.');
        }

        productWasAdded = true;
        this.#confirmedProductIds.add(String(productId));
        this.#setRecommendationAddState(button, 'success', { announce: true });
        this.#refreshRecommendations();
        return;
      } finally {
        window.clearTimeout(addTimeout);
      }

      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Kunde inte lägga till produkten.');

      const addedItem = Array.isArray(data.items)
        ? data.items.find((item) => String(item.variant_id) === String(variantId))
        : null;
      expectedLineKey = String(addedItem?.key || '');
      expectedLineQuantity = Number(addedItem?.quantity);
      hasExactLineExpectation =
        Boolean(expectedLineKey) && Number.isFinite(expectedLineQuantity) && expectedLineQuantity > 0;

      productWasAdded = true;
      this.#confirmedProductIds.add(String(productId));

      let cartData = data;
      if (!cartData.sections?.[sectionId]) {
        cartData = await fetchCurrentSection(data);
      }

      let sectionError;
      let sectionApplied = false;
      try {
        sectionApplied = this.#applyDrawerSectionUpdate(cartData);
      } catch (error) {
        sectionError = error;
      }

      let quantityApplied = isAddedQuantityRendered();
      if ((!sectionApplied || !quantityApplied) && this.#dialog?.open && !fallbackSectionFetched) {
        cartData = await fetchCurrentSection(data);
        try {
          sectionApplied = this.#applyDrawerSectionUpdate(cartData);
          sectionError = undefined;
        } catch (error) {
          sectionApplied = false;
          sectionError = error;
        }
        quantityApplied = isAddedQuantityRendered();
      }

      if (sectionApplied && quantityApplied) {
        this.#setRecommendationAddState(button, 'success', { announce: true });
        this.#refreshRecommendations();
        return;
      }

      if (!this.#dialog?.open) {
        this.dispatchEvent(
          new CartAddEvent(cartData, variantId, {
            source: 'dj-cart-recommendations',
            itemCount: Math.max(0, itemCountBefore) + 1,
            productId,
            sections: cartData.sections,
          })
        );
        return;
      }

      throw sectionError || new Error('Produkten lades till, men varukorgen kunde inte visas direkt.');
    } catch (error) {
      if (productWasAdded) {
        this.#setRecommendationAddState(button, 'success', {
          message: error?.message || 'Produkten är tillagd', announce: true,
        });
        this.#refreshRecommendations();
        return;
      }
      button.disabled = false;
      this.#setRecommendationAddState(button, 'error', {
        message: error?.message || 'Kunde inte lägga till produkten', announce: true,
      });
      const errorStateVersion = button.dataset.addStateVersion;
      window.setTimeout(() => {
        if (!button.isConnected || button.dataset.addState !== 'error' || button.dataset.addStateVersion !== errorStateVersion) return;
        this.#setRecommendationAddState(button, 'idle');
      }, 2500);
    }
  }

  #variantImage(variant, product) {
    const imageUrl = (image) => {
      if (typeof image === 'string') return image;
      return image?.src || image?.url || image?.preview_image?.src || image?.preview_image?.url || '';
    };
    const images = Array.isArray(product.images) ? product.images : [];
    const assignedImage = images.find((image) =>
      (variant?.image_id != null && String(image?.id) === String(variant.image_id)) ||
      image?.variant_ids?.some((id) => String(id) === String(variant?.id))
    );
    return imageUrl(variant?.featured_image) || imageUrl(variant?.featured_media) ||
      imageUrl(assignedImage) || imageUrl(product.featured_image) || imageUrl(images[0]);
  }

  #formatSavings(cents) {
    return this.#formatMoney(Math.ceil(Number(cents || 0) / 100) * 100);
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'SEK';
    const locale = document.documentElement.lang || 'sv-SE';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
  }
}

document.addEventListener(CartAddEvent.eventName, handleReservationCartEvent);
if (!customElements.get('dj-cart-reservation')) customElements.define('dj-cart-reservation', DjCartReservation);
if (!customElements.get('dj-cart-recommendations')) customElements.define('dj-cart-recommendations', DjCartRecommendations);
